import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Template } from "@/types/template";
import { Document } from "@/types/document";
import "@/styles/TemplateDocument.css";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { EditableTemplateContent } from "@/components/template/EditableTemplateContent";
import { PagedDocumentView } from "@/components/template/PagedDocumentView";
import { SaveDocumentDialog } from "@/components/SaveDocumentDialog";
import { updatePlaceholders } from "@/utils/templateProcessor";
import { 
  Card, 
  CardContent, 
  CardHeader,
  CardTitle,
  CardDescription 
} from "@/components/ui/card";

import { PlaceholderForm } from "@/components/PlaceholderForm";
import { useDocumentOperations } from "@/hooks/use-document-operations";
import { VersionSelector } from "@/components/document-download/VersionSelector";
import type { DocumentVersion } from "@/types/document";
import { FileText, Layout } from "lucide-react";

const formatPlaceholder = (placeholder: string): string => {
  return placeholder
    .replace(/^field_/, '') // remove 'field_' prefix
    .replace(/_/g, ' ') // replace underscores with spaces
    .replace(/ in rs/g, ' (in Rs.)') // special case for 'in rs'
    .replace(/\b\w/g, char => char.toUpperCase()); // capitalize each word
};

export default function DocumentEditor() {
  const { id } = useParams();
  const location = useLocation();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [initialContent, setInitialContent] = useState<string>("");
  const [modifiedContent, setModifiedContent] = useState<string>("");
  const [userId, setUserId] = useState<string | null>(null);
  const [currentValues, setCurrentValues] = useState<Record<string, string>>({});
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  const [allPlaceholders, setAllPlaceholders] = useState<string[]>([]);
  const [documentTitle, setDocumentTitle] = useState("Untitled Document");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<DocumentVersion | null>(null);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [viewMode, setViewMode] = useState<'continuous' | 'paged'>('continuous');
  


  const isEditingDocument = location.pathname.includes('/documents/');
  const { updateDocumentMutation, useDocumentVersions, fetchDocumentVersion } = useDocumentOperations();

  useEffect(() => {
    const getUserId = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) setUserId(session.user.id);
    };
    getUserId();
  }, []);

  const { data: document, isLoading: documentIsLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase.from('documents').select('*').eq('id', id).single();
      if (error) throw error;
      return data as Document;
    },
    enabled: isEditingDocument && !!id,
  });

  const templateIdToFetch = isEditingDocument ? document?.template_id : id;

  const { data: template, isLoading: templateIsLoading } = useQuery({
    queryKey: ['template', templateIdToFetch],
    queryFn: async () => {
        if (!templateIdToFetch) return null;
        const { data, error } = await supabase.from('templates').select('*').eq('id', templateIdToFetch).single();
        if (error) throw error;
        
        if (data.processed_html) {
            try {
                const { data: fileData, error: downloadError } = await supabase.storage
                    .from('template_html').download(data.processed_html);
                if (downloadError) {
                    console.error('Storage download error:', downloadError);
                    // Fall back to original processed_html if file doesn't exist or no permission
                    return { ...data, processed_html: data.processed_html } as Template;
                }
                const htmlContent = await fileData.text();
                return { ...data, processed_html: htmlContent } as Template;
            } catch (error) {
                console.error('Failed to download template HTML:', error);
                // Fall back to original processed_html content
                return { ...data, processed_html: data.processed_html } as Template;
            }
        }
        return data as Template;
    },
    enabled: !!templateIdToFetch,
  });

  const isLoading = documentIsLoading || templateIsLoading;

  // Fetch document versions when editing a document
  const { data: versions, isLoading: versionsLoading } = useDocumentVersions(
    isEditingDocument ? id : null
  );

  useEffect(() => {
    if (isEditingDocument) {
      if (document && !selectedVersion) {
        setInitialContent(document.content);
        setCurrentValues(document.placeholder_values || {});
        setDocumentTitle(document.filename);
      }
    } else {
      if (template) {
        setInitialContent(template.processed_html || "");
        setDocumentTitle(template.filename);
        setTemplateId(template.id);
        if (template.placeholders) {
          const initialValues = template.placeholders.reduce((acc, ph) => ({ ...acc, [ph]: '' }), {});
          setCurrentValues(initialValues);
        }
      }
    }
  }, [document, template, isEditingDocument, selectedVersion]);

  useEffect(() => {
    if (template) {
      setTemplateId(template.id);
      if (template.metadata?.displayNames) {
        setDisplayNames(template.metadata.displayNames);
      } else if (template.placeholders) {
        const generatedDisplayNames: Record<string, string> = {};
        template.placeholders.forEach((placeholder: string) => {
          generatedDisplayNames[placeholder] = formatPlaceholder(placeholder);
        });
        setDisplayNames(generatedDisplayNames);
      }
      
      if (template.placeholders) {
        setAllPlaceholders(template.placeholders);
      }
    }
  }, [template]);

  const updatePreview = useCallback((html: string, values: Record<string, string>) => {
    const updatedContent = updatePlaceholders(html, values);
    setModifiedContent(updatedContent);
  }, []);

  const handlePlaceholderChange = useCallback((placeholder: string, value: string) => {
    const newValues = { ...currentValues, [placeholder]: value };
    setCurrentValues(newValues);
    updatePreview(modifiedContent || initialContent, newValues);
  }, [currentValues, modifiedContent, initialContent, updatePreview]);
  
  const handleMultiplePlaceholderChange = (values: Record<string, string>) => {
    setCurrentValues(values);
    updatePreview(modifiedContent || initialContent, values);
  };
  
  const handleContentChange = useCallback((newContent: string) => {
    setModifiedContent(newContent);
    updatePreview(newContent, currentValues);
  }, [currentValues, updatePreview]);
  
  useEffect(() => {
    if (initialContent) {
      updatePreview(initialContent, currentValues);
    }
  }, [initialContent, currentValues, updatePreview]);




  const handleSave = async (filename?: string) => {
    if (!userId) return;
    const contentToSave = modifiedContent || initialContent;

    if (isEditingDocument && id) {
      // If editing a specific version, create a new version from it
      if (selectedVersion && selectedVersion.version_number !== document?.version_number) {
        toast({
          title: "Info",
          description: `Creating new version from version ${selectedVersion.version_number}`,
        });
      }
      
      await updateDocumentMutation.mutateAsync({
        documentId: id,
        content: contentToSave,
        placeholderValues: currentValues,
        filename,
      });
      toast({ title: "Success", description: "Document updated successfully." });
      navigate('/documents');
    } else if (templateId && filename) {
      const filePath = `${userId}/${crypto.randomUUID()}.html`;
      const htmlBlob = new Blob([contentToSave], { type: 'text/html' });

      const { error: uploadError } = await supabase.storage.from('documents').upload(filePath, htmlBlob);
      if (uploadError) throw uploadError;

      const { data: newDoc, error: insertError } = await supabase.from('documents').insert({
        template_id: templateId,
        user_id: userId,
        filename,
        content: contentToSave,
        file_path: filePath,
        placeholder_values: currentValues,
        version_number: 1,
      }).select().single();

      if (insertError) {
        await supabase.storage.from('documents').remove([filePath]);
        throw insertError;
      }

      await supabase.from('document_versions').insert({
          document_id: newDoc.id,
          version_number: 1,
          content: contentToSave,
          placeholder_values: currentValues,
          created_by: userId,
      });

      toast({ title: "Success", description: "Document saved successfully" });
      navigate('/documents');
    }
  };

  const handleVersionSelect = async (version: DocumentVersion) => {
    try {
      setSelectedVersion(version);
      setInitialContent(version.content);
      setCurrentValues(version.placeholder_values || {});
      setShowVersionSelector(false);
      
      toast({
        title: "Version Loaded",
        description: `Now editing version ${version.version_number}`,
      });
    } catch (error) {
      console.error('Error loading version:', error);
      toast({
        title: "Error",
        description: "Failed to load document version",
        variant: "destructive",
      });
    }
  };

  const handleSaveInitiation = () => {
    if (isEditingDocument) {
      handleSave(documentTitle);
    } else {
      setShowSaveDialog(true);
    }
  };
  
  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <div className="flex flex-col h-screen">
        <header className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">{isEditingDocument ? 'Edit Document' : 'Create Document'}: {documentTitle}</h1>
            {isEditingDocument && versions && versions.length > 1 && (
              <VersionSelector
                versions={versions}
                currentVersion={selectedVersion?.version_number || document?.version_number || 1}
                onVersionSelect={handleVersionSelect}
                isLoading={versionsLoading}
              />
            )}
            {selectedVersion && (
              <div className="text-sm text-muted-foreground">
                Editing version {selectedVersion.version_number}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center border rounded-lg p-1">
              <Button
                variant={viewMode === 'continuous' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('continuous')}
                className="flex items-center gap-1"
              >
                <FileText className="h-4 w-4" />
                Continuous
              </Button>
              <Button
                variant={viewMode === 'paged' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('paged')}
                className="flex items-center gap-1"
              >
                <Layout className="h-4 w-4" />
                Page View
              </Button>
            </div>
            <Button onClick={() => setSidebarOpen(!sidebarOpen)} variant="outline" size="icon">
              {sidebarOpen ? "←" : "→"}
            </Button>
            <Button onClick={handleSaveInitiation} size="lg">
              Save
              {isEditingDocument ? " Changes" : " Document"}
            </Button>
          </div>
        </header>
        <div className="flex flex-1 overflow-hidden">
          <main className="flex-1 overflow-y-auto">
            {viewMode === 'continuous' ? (
              <div className="p-6">
                <Card className="template-page">
                  <CardContent className="p-0">
                    <EditableTemplateContent
                      content={modifiedContent || initialContent}
                      onContentChange={handleContentChange}
                      onPlaceholderChange={handlePlaceholderChange}
                      placeholderValues={currentValues}
                    />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <PagedDocumentView
                content={modifiedContent || initialContent}
                onContentChange={handleContentChange}
                onPlaceholderChange={handlePlaceholderChange}
                placeholderValues={currentValues}
              />
            )}
          </main>
          {sidebarOpen && (
            <aside className="w-96 border-l overflow-y-auto p-4">
              <Card>
                <CardHeader>
                  <CardTitle>Document Fields</CardTitle>
                  <CardDescription>Fill in the placeholders to customize your document.</CardDescription>
                </CardHeader>
                <CardContent>
                  <PlaceholderForm
                    placeholders={allPlaceholders}
                    displayNames={displayNames}
                    onValuesChange={handleMultiplePlaceholderChange}
                    initialValues={currentValues}
                  />
                </CardContent>
              </Card>
            </aside>
          )}
        </div>
      </div>
      <SaveDocumentDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={handleSave}
        defaultFilename={documentTitle}
      />
    </>
  );
}
